import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../App';
import {
  MessageSquare, PoundSterling, XCircle, UserMinus,
  CheckCircle2, AlertTriangle, RefreshCcw, Send,
} from 'lucide-react';

/**
 * Admin Messaging tab, SMS operational dashboard.
 *
 * Shows headline KPIs (total / delivered / failed / success rate / est. cost),
 * a per-category breakdown, a per-error-code failure breakdown, and the most
 * recent 50 SMS log entries with phone numbers masked.
 *
 * Data source: /api/admin/sms-stats + /api/admin/sms-logs (both admin-gated).
 */

const STATUS_STYLES = {
  delivered:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  queued:         'bg-stone-50 text-stone-600 border-stone-200',
  sent:           'bg-blue-50 text-blue-700 border-blue-200',
  failed:         'bg-red-50 text-red-700 border-red-200',
  undelivered:    'bg-red-50 text-red-700 border-red-200',
  unsubscribed:   'bg-amber-50 text-amber-700 border-amber-200',
  invalid_phone:  'bg-stone-50 text-stone-600 border-stone-200',
  not_configured: 'bg-stone-50 text-stone-600 border-stone-200',
  suppressed:     'bg-stone-50 text-stone-600 border-stone-200',
};

// Human-friendly labels for known Twilio error codes.
const ERROR_LABELS = {
  '30001': 'Queue overflow',
  '30002': 'Account suspended',
  '30003': 'Unreachable (handset off / out of service)',
  '30004': 'Message blocked by carrier',
  '30005': 'Unknown destination',
  '30006': 'Landline or unreachable carrier',
  '30007': 'Carrier filtered as spam',
  '30008': 'Unknown error',
  '21610': 'Recipient has replied STOP (opt-out)',
  '21614': 'Invalid mobile number',
};

function Tile({ icon: Icon, label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-[#D4AF37]',
    good:    'text-emerald-600',
    warn:    'text-amber-600',
    bad:     'text-red-600',
  };
  return (
    <div className="card-surface p-4" data-testid={`sms-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${tones[tone]}`} />
        <span className="text-xs text-stone-500">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-[#1A1A1A]">{value}</p>
      {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${cls}`}>
      {status}
    </span>
  );
}

export default function AdminMessagingTab() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(7);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l] = await Promise.all([
        api.get('/admin/sms-stats', { params: { days: windowDays } }),
        api.get('/admin/sms-logs', { params: { limit: 50, status: statusFilter || undefined } }),
      ]);
      setStats(s.data);
      setLogs(l.data?.logs || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load SMS stats');
    } finally {
      setLoading(false);
    }
  }, [windowDays, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const successRate = stats?.success_rate_pct;
  const successTone = successRate == null
    ? 'neutral'
    : successRate >= 95 ? 'good'
    : successRate >= 85 ? 'warn'
    : 'bad';

  return (
    <div className="space-y-5" data-testid="admin-messaging-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">SMS operations</h3>
          <p className="text-xs text-stone-500 mt-0.5">
            Outbound volume, deliverability, spend + opt-outs. Rolling window.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            data-testid="sms-window-select"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs text-stone-500 hover:text-[#D4AF37] inline-flex items-center gap-1 transition-colors disabled:opacity-50"
            data-testid="sms-refresh"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card-surface p-4 flex items-start gap-2 bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Tile icon={Send} label="Total sent" value={stats?.sent ?? ','}
              sub={stats ? `${stats.total_attempts} attempts` : undefined} />
        <Tile icon={CheckCircle2} label="Delivered" value={stats?.delivered ?? ','} tone="good"
              sub={successRate != null ? `${successRate}% success` : undefined} />
        <Tile icon={XCircle} label="Failed" value={stats?.failed ?? ','}
              tone={(stats?.failed ?? 0) > 0 ? 'bad' : 'neutral'} />
        <Tile icon={UserMinus} label="Opt-outs" value={stats?.unsubscribe_count ?? ','}
              sub="All-time STOP list" />
        <Tile icon={PoundSterling} label="Est. cost"
              value={stats ? `£${stats.estimated_cost_gbp.toFixed(2)}` : ','}
              sub={stats ? `${stats.estimated_segments} segments` : undefined} />
      </div>

      {/* By category + by error code */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-surface p-4" data-testid="sms-by-category">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-[#1A1A1A] text-sm">Volume by category</h4>
            <MessageSquare className="h-4 w-4 text-[#D4AF37]" />
          </div>
          {stats?.by_category?.length ? (
            <div className="space-y-2">
              {stats.by_category.map((c) => {
                const total = stats.by_category.reduce((a, b) => a + b.count, 0);
                const pct = total ? (c.count / total) * 100 : 0;
                return (
                  <div key={c.category} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-stone-600">{c.category.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-[#1A1A1A]">{c.count}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4AF37] transition-all"
                           style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-stone-400 py-4 text-center">No SMS activity in this window.</p>
          )}
        </div>

        <div className="card-surface p-4" data-testid="sms-by-error-code">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-[#1A1A1A] text-sm">Failures by Twilio error code</h4>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </div>
          {stats?.failures_by_error_code?.length ? (
            <div className="space-y-1.5">
              {stats.failures_by_error_code.map((f) => (
                <div key={f.error_code} className="flex items-center justify-between gap-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <code className="text-red-700 font-mono">{f.error_code}</code>
                    <span className="text-stone-500 ml-1.5">
                      {ERROR_LABELS[f.error_code] || 'Unknown error'}
                    </span>
                  </div>
                  <span className="font-medium text-[#1A1A1A]">{f.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-emerald-600 py-4 text-center">No failed deliveries. 🎉</p>
          )}
        </div>
      </div>

      {/* Recent log table */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[#1A1A1A] text-sm">Recent messages</h4>
          <select
            className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="sms-status-filter"
          >
            <option value="">All statuses</option>
            <option value="delivered">Delivered</option>
            <option value="queued">Queued</option>
            <option value="failed">Failed</option>
            <option value="undelivered">Undelivered</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="not_configured">Not configured</option>
          </select>
        </div>
        {logs.length === 0 ? (
          <p className="text-xs text-stone-400 py-6 text-center">No SMS activity matching the current filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sms-log-table">
              <thead>
                <tr className="text-left text-stone-500 border-b border-stone-200">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Phone</th>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Preview</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((r, idx) => (
                  <tr key={`${r.twilio_sid || 'nosid'}-${idx}`} className="border-b border-stone-100 last:border-0">
                    <td className="py-2 pr-3 text-stone-500 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ','}
                    </td>
                    <td className="py-2 pr-3 font-mono text-stone-600 whitespace-nowrap">{r.phone_masked || ','}</td>
                    <td className="py-2 pr-3 text-stone-600">{(r.category || 'unknown').replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-3">
                      <StatusPill status={r.status || 'unknown'} />
                      {r.error_code && <span className="ml-1.5 text-[10px] text-red-600 font-mono">{r.error_code}</span>}
                    </td>
                    <td className="py-2 pr-3 text-stone-500 max-w-[240px] truncate" title={r.body_preview}>{r.body_preview}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
